import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import Collapsible from '../../library/collapsible/collapsible';
import CollapsibleToggle from '../../library/collapsible/components/collapsible-toggle/collapsible-toggle';
import IntroductionMessage from './components/introduction-message/introduction-message';
import TreadwellAiSolidIcon from '../../shared/icons/treadwell-ai-solid-icon';

import { GET_TREADWELL_GEN_AI } from './graphql/treadwell-product-explanation-queries';

import {
  useFitment,
  useGraphQLClient,
  useMyStore,
  usePrevious,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { interpolateCmsString } from '../../shared/utils/cms/cms';
import { getVehicleInput } from '../../shared/utils/fitment/fitment';
import { getTreadwellAITrackActionPrefix } from '../../shared/utils/product/product';
import { getTreadwellGQLInput } from '../../shared/utils/treadwell/treadwell';
import { isArrayWithLength } from '../../shared/utils/validators/validators';
import { pickRandomLoadingMessage } from './utils/treadwell-product-explanation-utils';

import {
  ErrorData,
  Maybe,
  ProductData
} from '../../shared/types/graphql-types';
import { PageTypes } from '../../shared/constants/page-types';
import { TREADWELL_AI_TRACK_ACTION_KEYS } from '../../shared/constants/products';
import {
  TREADWELL_AI_LOADING_MESSAGES,
  TREADWELL_AI_MESSAGE_CHANGE_MS
} from './constants/treadwell-product-explanation-constants';

import './treadwell-product-explanation.scss';

const Explanation = lazy(() => import('./components/explanation/explanation'));

//TODO: Get from graphqlTypes when __FF_TREADWELL_AI_PDP__ & __FF_TREADWELL_AI_COMPARE__ is live
export enum RequestTypeEnum {
  BigCompare = 'BIGCOMPARE',
  ContextualCompare = 'CONTEXTUALCOMPARE',
  Explain = 'EXPLAINABILITY'
}

// TODO: Remove FFProductData & FFRecommendationData  when __FF_TREADWELL_AI_PDP__ & __FF_TREADWELL_AI_COMPARE__ is live
interface FFRecommendationData {
  treadwellSessionResponseCacheKey?: string;
}

interface FFProductData extends Omit<ProductData, 'recommendationData'> {
  recommendationData: ProductData['recommendationData'] & FFRecommendationData;
}

interface TreadwellProductExplanationProps {
  className?: string;
  compareSection?: React.ReactNode;
  isComparingProducts?: boolean;
  isOpenByDefault?: boolean;
  pageType: keyof typeof PageTypes;
  products?: Array<Maybe<FFProductData>>;
  requestType: RequestTypeEnum;
}

function TreadwellProductExplanation(props: TreadwellProductExplanationProps) {
  const {
    className,
    compareSection,
    isComparingProducts,
    isOpenByDefault = false,
    pageType,
    products = [],
    requestType
  } = props;
  const client = useGraphQLClient();
  const { myStore } = useMyStore();
  const { treadwellPreferences } = useTreadwellPreferences();
  const { selectedFitment } = useFitment();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<Maybe<string>>(null);
  const [explanation, setExplanation] = useState<Maybe<string>>(null);
  const [errors, setErrors] = useState<Maybe<Array<ErrorData>>>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [submittedFeedback, setSubmittedFeedback] = useState({});
  const { isEveryProductTreadwell, treadwellCacheKey } = useMemo(() => {
    const treadwellData = {
      isEveryProductTreadwell: true,
      treadwellCacheKey: ''
    };

    for (const product of products) {
      if (!product?.recommendationData) {
        treadwellData.isEveryProductTreadwell = false;
      }

      if (!treadwellData.treadwellCacheKey) {
        treadwellData.treadwellCacheKey =
          product?.recommendationData?.treadwellSessionResponseCacheKey || '';
      }

      if (
        treadwellData.treadwellCacheKey &&
        !treadwellData.isEveryProductTreadwell
      ) {
        break;
      }
    }

    return treadwellData;
  }, [products]);

  const trackActionIdPrefix = getTreadwellAITrackActionPrefix(
    pageType,
    isComparingProducts
  );
  const { zipCodeWeatherData } = treadwellPreferences || {};
  const { city } = zipCodeWeatherData || {};

  const loadingMessageIntervalRef: { current: NodeJS.Timeout | null } =
    useRef(null);
  const lastArrIndex = useRef<number>(-1);
  const lastMessage = useRef<Maybe<string>>(null);

  const previousProducts = usePrevious(products);

  const shouldUseDefaultExplanation = useMemo(
    () =>
      !isArrayWithLength(products) ||
      !treadwellPreferences ||
      !selectedFitment ||
      !isEveryProductTreadwell,
    [isEveryProductTreadwell, products, selectedFitment, treadwellPreferences]
  );
  const hasDefaultFetchedRef = useRef(false);
  const introductionMessageRef = useRef(null);
  const fetchCount = useRef(0);
  const collapsedButtonText = shouldUseDefaultExplanation
    ? 'Learn more'
    : 'See why';

  const handleErrors = useCallback(
    errors => {
      trackAction(`${trackActionIdPrefix}_error`, {
        [TREADWELL_AI_TRACK_ACTION_KEYS.ERROR_REASON]: errors?.[0]?.message
      });
      setErrors(errors);
    },
    [trackActionIdPrefix]
  );

  const handleMessageSet = useCallback(
    msg => {
      if (!introductionMessageRef.current) {
        introductionMessageRef.current = msg;

        trackAction(`${trackActionIdPrefix}_view`, {
          [TREADWELL_AI_TRACK_ACTION_KEYS.MESSAGE]: msg
        });
      }
    },
    [trackActionIdPrefix]
  );

  const handleLoadingMessage = useCallback(
    arrIdx => {
      const msg = pickRandomLoadingMessage(arrIdx, lastMessage.current);
      lastMessage.current = msg;

      const interpolatedMessage = interpolateCmsString(
        {
          city: city
            ? city.charAt(0)?.toUpperCase() + city?.slice(1).toLowerCase()
            : "your city's"
        },
        msg
      );
      setLoadingMessage(interpolatedMessage);
    },
    [city]
  );

  const handleTreadwellClick = useCallback(
    () =>
      trackAction(`${trackActionIdPrefix}_learn_more_treadwell_click`, {
        [TREADWELL_AI_TRACK_ACTION_KEYS.MESSAGE]: introductionMessageRef.current
      }),
    [trackActionIdPrefix]
  );

  const fetchTreadwellExplanation = useCallback(
    async (forceFetch = false) => {
      if (
        (!shouldUseDefaultExplanation &&
          !isLoading &&
          !errors &&
          fetchCount.current === 0) ||
        forceFetch
      ) {
        const treadwell = getTreadwellGQLInput(
          selectedFitment,
          treadwellPreferences
        );
        const vehicleInfo = getVehicleInput(selectedFitment);
        const startTime = performance.now();

        setIsLoading(true);
        setErrors(null);
        fetchCount.current++;
        handleLoadingMessage(0);
        lastArrIndex.current = 0;

        loadingMessageIntervalRef.current = setInterval(() => {
          const timeElapsed = performance.now() - startTime;
          const maxArrs = TREADWELL_AI_LOADING_MESSAGES.length - 1;
          const arrIdx = Math.min(
            Math.floor(timeElapsed / TREADWELL_AI_MESSAGE_CHANGE_MS),
            maxArrs
          );
          if (arrIdx !== lastArrIndex.current) {
            handleLoadingMessage(arrIdx);
            lastArrIndex.current = arrIdx;
          }
        }, TREADWELL_AI_MESSAGE_CHANGE_MS);

        const { data, errors } = await client.query({
          fetchPolicy: 'network-only',
          query: GET_TREADWELL_GEN_AI,
          variables: {
            productInfo: products.map(product => ({
              frontProductCode: product!.code,
              rearProductCode: product!.rearProduct?.code
            })),
            requestType: requestType,
            sourceType: 'WEB',
            storeCode: myStore?.code,
            treadwellCacheKey: treadwellCacheKey,
            treadwellRequest: treadwell,
            vehicleInfo
          }
        });
        const endTime = performance.now();
        const loadTime = Math.round((endTime - startTime) / 1000);

        const explanationData = data?.product?.treadwellGenAI;

        if (explanationData) {
          trackAction(`${trackActionIdPrefix}_see_why_loaded`, {
            [TREADWELL_AI_TRACK_ACTION_KEYS.LOAD_TIME]: `${loadTime}s`,
            [TREADWELL_AI_TRACK_ACTION_KEYS.MESSAGE]:
              introductionMessageRef.current
          });
          setExplanation(explanationData?.replace(/[•]/g, '-'));
        } else {
          handleErrors(
            errors || [{ message: 'Unable to fetch treadwell explanation' }]
          );
        }

        setIsLoading(false);
        setLoadingMessage(null);
        clearInterval(loadingMessageIntervalRef.current);
        loadingMessageIntervalRef.current = null;
      }
    },
    [
      client,
      errors,
      handleErrors,
      handleLoadingMessage,
      isLoading,
      myStore?.code,
      products,
      requestType,
      selectedFitment,
      shouldUseDefaultExplanation,
      trackActionIdPrefix,
      treadwellCacheKey,
      treadwellPreferences
    ]
  );

  const handleToggle = useCallback(() => {
    if (fetchCount.current === 0) {
      trackAction(
        `${trackActionIdPrefix}_${
          shouldUseDefaultExplanation ? 'learn_more' : 'see_why'
        }_click`,
        {
          [TREADWELL_AI_TRACK_ACTION_KEYS.MESSAGE]:
            introductionMessageRef.current
        }
      );
    }

    setShowExplanation(!showExplanation);
    fetchTreadwellExplanation();
  }, [
    fetchTreadwellExplanation,
    shouldUseDefaultExplanation,
    showExplanation,
    trackActionIdPrefix
  ]);

  const handleTryAgainClick = useCallback(() => {
    trackAction(`${trackActionIdPrefix}_error_retry`);
    fetchTreadwellExplanation(true);
  }, [fetchTreadwellExplanation, trackActionIdPrefix]);

  useEffect(() => {
    if (
      previousProducts?.length !== products?.length &&
      fetchCount.current > 0
    ) {
      setShowExplanation(false);
      fetchCount.current = 0;
    }
  }, [previousProducts, products, showExplanation]);

  useEffect(() => {
    if (isOpenByDefault && !hasDefaultFetchedRef.current) {
      hasDefaultFetchedRef.current = true;
      handleToggle();
    }
  }, [handleToggle, isOpenByDefault]);

  useEffect(
    () => () => {
      loadingMessageIntervalRef.current &&
        clearInterval(loadingMessageIntervalRef.current);
    },
    []
  );

  return (
    <div
      className={className}
      styleName={`container ${isLoading ? 'container--loading' : ''}`}
    >
      <div styleName="collapsible-toggle-container">
        <TreadwellAiSolidIcon styleName="treadwell-ai-icon" />
        <div styleName="introduction-container">
          <IntroductionMessage
            isComparingProducts={isComparingProducts}
            onMessageSet={handleMessageSet}
            products={products}
          />
          <CollapsibleToggle
            collapseType="SECONDARY"
            isOpen={showExplanation}
            label={showExplanation ? 'Hide details' : collapsedButtonText}
            styleName="collapsible-toggle"
            toggleCollapsible={handleToggle}
            toggleType="CHEVRON"
          />
        </div>
      </div>
      <Collapsible hideToggle isOpen={showExplanation} styleName="collapsible">
        <Suspense fallback={<div />}>
          <Explanation
            compareSection={compareSection}
            errors={errors}
            explanation={explanation}
            hideDisclaimer={shouldUseDefaultExplanation}
            hideReviewFeedback={shouldUseDefaultExplanation}
            id={treadwellCacheKey || products[0]?.code || ''}
            isLoading={isLoading}
            loadingMessage={loadingMessage}
            onError={handleErrors}
            onHelpfulClick={(id, isHelpful) => {
              trackAction(
                `${trackActionIdPrefix}_see_why_rate_response_click`,
                {
                  [TREADWELL_AI_TRACK_ACTION_KEYS.RESPONSE_RATING]: isHelpful
                    ? 'helpful'
                    : 'unhelpful'
                }
              );
              setSubmittedFeedback(prev => ({
                ...prev,
                [id]: isHelpful
              }));
            }}
            onTreadwellClick={handleTreadwellClick}
            onTryAgainClick={handleTryAgainClick}
            showErrorRefetchButton={fetchCount.current < 2}
            submittedFeedback={submittedFeedback}
          />
        </Suspense>
      </Collapsible>
    </div>
  );
}

export default TreadwellProductExplanation;
